import { PatientData, MentalStatusExam, Medication, SYMPTOM_OPTIONS, MSE_OPTIONS } from '../types';
import MedicationList from './MedicationList';

interface PatientFormProps {
  data: PatientData;
  onChange: (data: PatientData) => void;
}

export default function PatientForm({ data, onChange }: PatientFormProps) {
  const updateField = <K extends keyof PatientData>(field: K, value: PatientData[K]) => {
    onChange({ ...data, [field]: value });
  };

  const updateMSE = <K extends keyof MentalStatusExam>(field: K, value: string) => {
    onChange({
      ...data,
      mentalStatusExam: { ...data.mentalStatusExam, [field]: value }
    });
  };

  const toggleSymptom = (symptom: string) => {
    const newSymptoms = data.symptoms.includes(symptom)
      ? data.symptoms.filter(s => s !== symptom)
      : [...data.symptoms, symptom];
    updateField('symptoms', newSymptoms);
  };

  return (
    <div className="space-y-6">
      {/* Demographics Section */}
      <div className="section-card">
        <h2 className="section-title">Patient Demographics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Patient Name</label>
            <input
              type="text"
              value={data.patientName}
              onChange={(e) => updateField('patientName', e.target.value)}
              placeholder="John Doe"
              className="input-field"
            />
          </div>
          <div>
            <label className="label">Date of Birth</label>
            <input
              type="date"
              value={data.dateOfBirth}
              onChange={(e) => updateField('dateOfBirth', e.target.value)}
              className="input-field"
            />
          </div>
          <div>
            <label className="label">MRN (Medical Record Number)</label>
            <input
              type="text"
              value={data.mrn}
              onChange={(e) => updateField('mrn', e.target.value)}
              placeholder="MRN-123456"
              className="input-field"
            />
          </div>
          <div>
            <label className="label">Date of Service</label>
            <input
              type="date"
              value={data.dateOfService}
              onChange={(e) => updateField('dateOfService', e.target.value)}
              className="input-field"
            />
          </div>
          <div className="md:col-span-2">
            <label className="label">Provider Name</label>
            <input
              type="text"
              value={data.provider}
              onChange={(e) => updateField('provider', e.target.value)}
              placeholder="Demo Provider, MD"
              className="input-field"
            />
          </div>
        </div>
      </div>

      {/* Chief Complaint & HPI Section */}
      <div className="section-card">
        <h2 className="section-title">Clinical Information</h2>
        <div className="space-y-4">
          <div>
            <label className="label">Chief Complaint</label>
            <input
              type="text"
              value={data.chiefComplaint}
              onChange={(e) => updateField('chiefComplaint', e.target.value)}
              placeholder="Patient's main concern in their own words"
              className="input-field"
            />
          </div>
          <div>
            <label className="label">History of Present Illness</label>
            <textarea
              value={data.historyOfPresentIllness}
              onChange={(e) => updateField('historyOfPresentIllness', e.target.value)}
              placeholder="Describe the onset, duration, severity, and progression of symptoms..."
              className="textarea-field"
              rows={4}
            />
          </div>
        </div>
      </div>

      {/* Symptoms Checklist */}
      <div className="section-card">
        <h2 className="section-title">Symptoms Checklist</h2>
        <p className="text-sm text-gray-500 mb-4">Select all symptoms currently present</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {SYMPTOM_OPTIONS.map(symptom => (
            <label
              key={symptom}
              className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                data.symptoms.includes(symptom)
                  ? 'bg-clinical-50 border border-clinical-200'
                  : 'hover:bg-gray-50 border border-transparent'
              }`}
            >
              <input
                type="checkbox"
                checked={data.symptoms.includes(symptom)}
                onChange={() => toggleSymptom(symptom)}
                className="w-4 h-4 text-clinical-600 rounded focus:ring-clinical-500"
              />
              <span className="text-sm">{symptom}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Medications Section */}
      <div className="section-card">
        <h2 className="section-title">Current Medications</h2>
        <MedicationList
          medications={data.medications}
          onChange={(meds: Medication[]) => updateField('medications', meds)}
        />
      </div>

      {/* Mental Status Exam Section */}
      <div className="section-card">
        <h2 className="section-title">Mental Status Examination</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Appearance</label>
            <select
              value={data.mentalStatusExam.appearance}
              onChange={(e) => updateMSE('appearance', e.target.value)}
              className="select-field"
            >
              <option value="">Select...</option>
              {MSE_OPTIONS.appearance.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Behavior</label>
            <select
              value={data.mentalStatusExam.behavior}
              onChange={(e) => updateMSE('behavior', e.target.value)}
              className="select-field"
            >
              <option value="">Select...</option>
              {MSE_OPTIONS.behavior.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Speech</label>
            <select
              value={data.mentalStatusExam.speech}
              onChange={(e) => updateMSE('speech', e.target.value)}
              className="select-field"
            >
              <option value="">Select...</option>
              {MSE_OPTIONS.speech.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Mood (Patient Reported)</label>
            <select
              value={data.mentalStatusExam.mood}
              onChange={(e) => updateMSE('mood', e.target.value)}
              className="select-field"
            >
              <option value="">Select...</option>
              {MSE_OPTIONS.mood.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Affect (Observed)</label>
            <select
              value={data.mentalStatusExam.affect}
              onChange={(e) => updateMSE('affect', e.target.value)}
              className="select-field"
            >
              <option value="">Select...</option>
              {MSE_OPTIONS.affect.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Thought Process</label>
            <select
              value={data.mentalStatusExam.thoughtProcess}
              onChange={(e) => updateMSE('thoughtProcess', e.target.value)}
              className="select-field"
            >
              <option value="">Select...</option>
              {MSE_OPTIONS.thoughtProcess.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Thought Content</label>
            <select
              value={data.mentalStatusExam.thoughtContent}
              onChange={(e) => updateMSE('thoughtContent', e.target.value)}
              className="select-field"
            >
              <option value="">Select...</option>
              {MSE_OPTIONS.thoughtContent.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Cognition</label>
            <select
              value={data.mentalStatusExam.cognition}
              onChange={(e) => updateMSE('cognition', e.target.value)}
              className="select-field"
            >
              <option value="">Select...</option>
              {MSE_OPTIONS.cognition.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="label">Insight/Judgment</label>
            <select
              value={data.mentalStatusExam.insightJudgment}
              onChange={(e) => updateMSE('insightJudgment', e.target.value)}
              className="select-field"
            >
              <option value="">Select...</option>
              {MSE_OPTIONS.insightJudgment.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Assessment & Plan Section */}
      <div className="section-card">
        <h2 className="section-title">Assessment & Plan</h2>
        <div className="space-y-4">
          <div>
            <label className="label">Assessment/Diagnosis</label>
            <textarea
              value={data.assessment}
              onChange={(e) => updateField('assessment', e.target.value)}
              placeholder="Diagnoses with ICD-10 codes if applicable..."
              className="textarea-field"
              rows={3}
            />
          </div>
          <div>
            <label className="label">Plan/Recommendations</label>
            <textarea
              value={data.plan}
              onChange={(e) => updateField('plan', e.target.value)}
              placeholder="1. Treatment recommendations&#10;2. Medication changes&#10;3. Follow-up schedule"
              className="textarea-field"
              rows={4}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
